/**
 * PageHeader — W66.X26 universal back button + breadcrumb strip.
 *
 * Rendered once inside OperatorTopbar, so every page wrapped in OperatorShell
 * inherits it automatically — no per-page wiring. The back button uses browser
 * history; the breadcrumb is derived from the current hash route via a hardcoded
 * route → section → page map (ROUTE_META below). Section labels are display
 * strings that mirror the left-nav sidebar grouping (Operate / Plan / Reference
 * / Galaxy), so we do not derive them from the URL alone.
 *
 * Format: Home › Section › Page
 *   - Home links to /today (the authenticated home).
 *   - Section is clickable when it has a canonical landing route.
 *   - Page is the current page (not a link).
 *
 * The back button is hidden on /today (the home page) since there is nowhere
 * further back to go inside the app.
 *
 * Mobile: the middle (section) segment truncates first; Home and the current
 * page are always preserved.
 *
 * Brand: gray #6B7280 muted text, red #DC2626 accents only. No other hues.
 */
import { useLocation, Link } from "wouter";
import { ChevronLeft, ChevronRight } from "lucide-react";

const GRAY = "#6B7280";
const RED = "#DC2626";
const INK = "#0B0B0F";

type Section = {
  /** Display label, mirrors the left-nav section header. */
  label: string;
  /** Optional canonical landing route for the section (makes it clickable). */
  href?: string;
};

const SECTION_OPERATE: Section = { label: "Operate" };
const SECTION_PLAN: Section = { label: "Plan" };
const SECTION_REFERENCE: Section = { label: "Reference" };
const SECTION_GALAXY: Section = { label: "Galaxy", href: "/galaxy" };

type RouteMeta = {
  /** Exact route to match, or a prefix when `prefix` is true. */
  path: string;
  prefix?: boolean;
  section: Section;
  page: string;
};

/**
 * Route → section → page-name map. Order matters: more specific prefixes are
 * listed before broader ones. The first match (exact, then prefix) wins.
 * Section grouping mirrors OperatorSidebar (Operate / Plan / Reference) plus
 * Galaxy, which the sidebar treats as its own hero destination.
 */
const ROUTE_META: RouteMeta[] = [
  // ── OPERATE ──────────────────────────────────────────────────────────────
  { path: "/workspace", prefix: true, section: SECTION_OPERATE, page: "Workspace" },
  { path: "/today", section: SECTION_OPERATE, page: "Today" },
  { path: "/my-plan", section: SECTION_OPERATE, page: "My Plan" },
  { path: "/jumpstart", prefix: true, section: SECTION_OPERATE, page: "Jumpstart" },
  { path: "/command-view", prefix: true, section: SECTION_OPERATE, page: "Command View" },
  { path: "/accounts/new", section: SECTION_OPERATE, page: "New Account" },
  { path: "/accounts", prefix: true, section: SECTION_OPERATE, page: "Accounts" },
  { path: "/account", prefix: true, section: SECTION_OPERATE, page: "Account" },

  // ── PLAN ─────────────────────────────────────────────────────────────────
  { path: "/methodology", prefix: true, section: SECTION_PLAN, page: "Methodology" },
  { path: "/question-bank", prefix: true, section: SECTION_PLAN, page: "Question Bank" },
  { path: "/sales-plan", prefix: true, section: SECTION_PLAN, page: "Sales Plan" },
  { path: "/funnel", prefix: true, section: SECTION_PLAN, page: "Funnel" },

  // ── REFERENCE ──────────────────────────────────────────────────────────────
  { path: "/manuals", prefix: true, section: SECTION_REFERENCE, page: "Manuals" },
  { path: "/library", prefix: true, section: SECTION_REFERENCE, page: "Reference Library" },
  { path: "/flowcharts", prefix: true, section: SECTION_REFERENCE, page: "Flowcharts" },
  { path: "/templates", prefix: true, section: SECTION_REFERENCE, page: "Templates" },

  // ── GALAXY ───────────────────────────────────────────────────────────────
  { path: "/galaxy", prefix: true, section: SECTION_GALAXY, page: "Galaxy" },
  { path: "/opportunity-dna", prefix: true, section: SECTION_GALAXY, page: "Opportunity DNA" },
  { path: "/tower", section: SECTION_GALAXY, page: "Tower" },
  { path: "/pipeline-tower", section: SECTION_GALAXY, page: "Pipeline Tower" },
  { path: "/pipeline-dna", prefix: true, section: SECTION_GALAXY, page: "Pipeline DNA" },
  { path: "/team-dna", prefix: true, section: SECTION_GALAXY, page: "Team DNA" },

  // ── Settings (Operate-adjacent; sidebar lists it under Admin/all-roles) ────
  { path: "/settings", prefix: true, section: SECTION_OPERATE, page: "Settings" },
];

const HOME_ROUTE = "/today";

/** Resolve the current location to its route metadata, or null if unmapped. */
function resolveMeta(location: string): RouteMeta | null {
  // Exact matches first.
  for (const m of ROUTE_META) {
    if (!m.prefix && m.path === location) return m;
  }
  // Then prefix matches (longest path wins to avoid /account shadowing /accounts).
  let best: RouteMeta | null = null;
  for (const m of ROUTE_META) {
    if (!m.prefix) continue;
    if (location === m.path || location.startsWith(m.path + "/")) {
      if (!best || m.path.length > best.path.length) best = m;
    }
  }
  return best;
}

export default function PageHeader() {
  const [location] = useLocation();
  const meta = resolveMeta(location);

  // Unmapped route — render nothing rather than a misleading crumb trail.
  if (!meta) return null;

  const isHome = location === HOME_ROUTE;
  const sep = (
    <ChevronRight
      size={13}
      strokeWidth={2}
      aria-hidden="true"
      style={{ color: "#C9C9CC", flexShrink: 0 }}
    />
  );

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        minWidth: 0,
      }}
    >
      {/* Back button — browser history. Hidden on the home page. */}
      {!isHome && (
        <button
          type="button"
          aria-label="Go back"
          onClick={() => window.history.back()}
          className="aip-pageheader-back"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 30,
            height: 30,
            flexShrink: 0,
            borderRadius: 6,
            background: "transparent",
            border: "1px solid #E5E7EB",
            cursor: "pointer",
            color: GRAY,
            padding: 0,
            transition: "color 0.12s, border-color 0.12s",
          }}
          onMouseEnter={(e) => {
            const b = e.currentTarget as HTMLButtonElement;
            b.style.color = RED;
            b.style.borderColor = RED;
          }}
          onMouseLeave={(e) => {
            const b = e.currentTarget as HTMLButtonElement;
            b.style.color = GRAY;
            b.style.borderColor = "#E5E7EB";
          }}
        >
          <ChevronLeft size={16} strokeWidth={2} />
        </button>
      )}

      {/* Breadcrumb: Home › Section › Page */}
      <nav
        aria-label="Breadcrumb"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 13,
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        {/* Home */}
        <Link
          href={HOME_ROUTE}
          style={{ color: GRAY, textDecoration: "none", flexShrink: 0, whiteSpace: "nowrap" }}
        >
          Home
        </Link>

        {sep}

        {/* Section — clickable when it has a landing route. Truncates first on
            narrow viewports via the responsive class below. */}
        <span
          className="aip-pageheader-section"
          style={{
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {meta.section.href ? (
            <Link
              href={meta.section.href}
              style={{ color: GRAY, textDecoration: "none" }}
            >
              {meta.section.label}
            </Link>
          ) : (
            <span style={{ color: GRAY }}>{meta.section.label}</span>
          )}
        </span>

        {sep}

        {/* Current page — never truncated, never a link. */}
        <span
          style={{
            color: INK,
            fontWeight: 600,
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {meta.page}
        </span>
      </nav>
    </div>
  );
}
